import React, { useEffect, useState, useRef } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import pb from '../../lib/pocketbase';
import './CommercialLayout.css';
import { useAuth } from '../../contexts/AuthContext';
import AuthModal from '../auth/AuthModal';

function CommercialLayout({ title = 'Commercial', children }) {
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [theme, setTheme] = useState(() => {
        try {
            const saved = localStorage.getItem('theme');
            if (saved === 'dark' || saved === 'light') return saved;
        } catch { }
        if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
            return 'dark';
        }
        return 'light';
    });
    const [collapsed, setCollapsed] = useState(() => {
        try {
            return localStorage.getItem('commercialSidebarCollapsed') === 'true';
        } catch { }
        return false;
    });

    const { user: currentUser, logout } = useAuth();
    const [authOpen, setAuthOpen] = useState(false);
    const [authMode, setAuthMode] = useState('login');
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const userMenuRef = useRef(null);
    const sidebarRef = useRef(null);

    // Secret Zone State
    const [secretUnlock, setSecretUnlock] = useState(false);
    const [secretCode, setSecretCode] = useState('');

    const handleSecretSubmit = (e) => {
        e.preventDefault();
        if (secretCode === 'test1234') {
            setSecretUnlock(true);
            setSecretCode('');
        } else {
            alert('Code incorrect');
        }
    };

    // Responsive state
    const [isDesktop, setIsDesktop] = useState(() => (typeof window !== 'undefined' ? window.innerWidth >= 1024 : true));

    // Profile Gamification State
    const [profileLevel, setProfileLevel] = useState(0);
    const [loadingProfile, setLoadingProfile] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        if (!currentUser) return;
        async function checkProfile() {
            try {
                // Check if profile exists and get level
                const p = await pb.collection('commercial_profiles').getFirstListItem(`user="${currentUser.id}"`);
                if (p) {
                    setProfileLevel(p.level || 0);
                } else {
                    setProfileLevel(0);
                }
            } catch (err) {
                // 404 or other error -> Profile invalid
                setProfileLevel(0);
            } finally {
                setLoadingProfile(false);
            }
        }
        checkProfile();
    }, [currentUser]);

    // Force Redirect / Gatekeeping
    useEffect(() => {
        if (loadingProfile) return;

        const path = location.pathname;

        // If Level 0, can only access Profile
        if (profileLevel === 0 && path !== '/commercial/profile') {
            navigate('/commercial/profile');
            return;
        }

        // If Level 1, can only access Profile & Formation
        if (profileLevel === 1 && path !== '/commercial/profile' && path !== '/commercial/formation') {
            navigate('/commercial/formation');
            return;
        }

        // If Level 2, can only access Profile, Formation & Admin
        if (profileLevel === 2 && path !== '/commercial/profile' && path !== '/commercial/formation' && path !== '/commercial/admin') {
            navigate('/commercial/admin');
            return;
        }

    }, [loadingProfile, profileLevel, location.pathname, navigate]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        let current = window.innerWidth >= 1024;
        setIsDesktop(current);
        const onResize = () => {
            const w = window.innerWidth;
            if (current) {
                if (w < 1000) { current = false; setIsDesktop(false); }
            } else {
                if (w > 1040) { current = true; setIsDesktop(true); }
            }
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // Click outside user menu
    useEffect(() => {
        const onDocClick = (e) => {
            if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
                setUserMenuOpen(false);
            }
        };
        document.addEventListener('click', onDocClick);
        return () => document.removeEventListener('click', onDocClick);
    }, []);

    // Theme Sync
    const didMountRef = useRef(false);
    useEffect(() => {
        document.body.classList.toggle('dark', theme === 'dark');
        if (didMountRef.current) {
            try { localStorage.setItem('theme', theme); } catch { }
        } else {
            didMountRef.current = true;
        }
    }, [theme]);

    // Sidebar persistence
    useEffect(() => {
        localStorage.setItem('commercialSidebarCollapsed', String(collapsed));
    }, [collapsed]);

    const handleBurger = () => {
        if (isDesktop) {
            setCollapsed(v => !v);
        } else {
            setOpen(v => !v);
        }
    };

    const themeClass = theme === 'dark' ? 'commercial-dark' : 'commercial-light';
    const sidebarClass = `commercial-sidebar ${(!isDesktop && open) ? 'open' : ''} ${collapsed ? 'collapsed' : ''}`;

    // Close sidebar on route change (mobile)
    useEffect(() => {
        if (!isDesktop) setOpen(false);
    }, [location.pathname, isDesktop]);

    return (
        <div className={`commercial-shell ${themeClass}`}>
            <aside ref={sidebarRef} className={sidebarClass}>
                <div className="brand">
                    <button
                        type="button"
                        className="brand-toggle"
                        onClick={handleBurger}
                        title={isDesktop ? (collapsed ? 'Ouvrir' : 'Réduire') : (open ? 'Fermer' : 'Ouvrir')}
                    >
                        {isDesktop ? (collapsed ? '▶' : '◀') : (open ? '◀' : '▶')}
                    </button>
                    {(!collapsed || !isDesktop) && <div className="title">Espace Pro</div>}
                </div>

                <nav className="commercial-nav">
                    <ul className="commercial-menu">
                        <li>
                            <NavLink to="/commercial/profile" className={({ isActive }) => isActive ? 'active' : ''} style={({ isActive }) => isActive ? { borderLeft: '4px solid #ef4444', background: 'rgba(239, 68, 68, 0.1)' } : {}}>
                                <span className="ico">👤</span>
                                <span className="item-label">Profil</span>
                            </NavLink>
                        </li>

                        <li style={profileLevel < 1 ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
                            <NavLink to="/commercial/formation" className={({ isActive }) => isActive ? 'active' : ''} style={{ color: profileLevel < 1 ? 'inherit' : '#22c55e' }}>
                                <span className="ico">{profileLevel < 1 ? '🔒' : '🎓'}</span>
                                <span className="item-label">Formation</span>
                            </NavLink>
                        </li>

                        <li style={profileLevel < 2 ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
                            <NavLink to="/commercial/admin" className={({ isActive }) => isActive ? 'active' : ''} style={{ color: profileLevel < 2 ? 'inherit' : '#22c55e' }}>
                                <span className="ico">{profileLevel < 2 ? '🔒' : '✍️'}</span>
                                <span className="item-label">Contrat</span>
                            </NavLink>
                        </li>

                        <li className="commercial-section">
                            <span className="label" style={collapsed ? { display: 'none' } : {}}>Espace Pro</span>
                        </li>

                        {/* LOCKED ITEMS GROUP - Unlocks at Level 3 */}
                        <div style={profileLevel < 3 ? { opacity: 0.4, pointerEvents: 'none', filter: 'grayscale(1)' } : {}}>
                            <li>
                                <NavLink to="/commercial/launchpad" className={({ isActive }) => isActive ? 'active' : ''} style={{ background: 'var(--commercial-accent)', color: 'white', fontWeight: 'bold' }}>
                                    <span className="ico">{profileLevel < 2 ? '🔒' : '📱'}</span>
                                    <span className="item-label">Mode Tablette</span>
                                </NavLink>
                            </li>
                            <li>
                                <NavLink to="/commercial" end className={({ isActive }) => isActive ? 'active' : ''}>
                                    <span className="ico">{profileLevel < 2 ? '🔒' : '📊'}</span>
                                    <span className="item-label">Tableau de bord</span>
                                </NavLink>
                            </li>
                            <li>
                                <NavLink to="/commercial/deals" className={({ isActive }) => isActive ? 'active' : ''}>
                                    <span className="ico">💼</span>
                                    <span className="item-label">Affaires en cours</span>
                                </NavLink>
                            </li>
                            <li>
                                <NavLink to="/commercial/commissions" className={({ isActive }) => isActive ? 'active' : ''}>
                                    <span className="ico">💰</span>
                                    <span className="item-label">Rémunération</span>
                                </NavLink>
                            </li>
                            <li className="commercial-section">
                                <span className="label" style={collapsed ? { display: 'none' } : {}}>Gestion</span>
                            </li>

                            <li>
                                <NavLink to="/commercial/clients" className={({ isActive }) => isActive ? 'active' : ''}>
                                    <span className="ico">{profileLevel < 2 ? '🔒' : '👥'}</span>
                                    <span className="item-label">Clients</span>
                                </NavLink>
                            </li>
                            <li>
                                <NavLink to="/commercial/devis" className={({ isActive }) => isActive ? 'active' : ''}>
                                    <span className="ico">{profileLevel < 2 ? '🔒' : '🧾'}</span>
                                    <span className="item-label">Devis</span>
                                </NavLink>
                            </li>
                            <li>
                                <NavLink to="/commercial/factures" className={({ isActive }) => isActive ? 'active' : ''}>
                                    <span className="ico">{profileLevel < 2 ? '🔒' : '💲'}</span>
                                    <span className="item-label">Factures</span>
                                </NavLink>
                            </li>
                            <li>
                                <NavLink to="/commercial/meetings" className={({ isActive }) => isActive ? 'active' : ''}>
                                    <span className="ico">{profileLevel < 2 ? '🔒' : '📅'}</span>
                                    <span className="item-label">Agenda</span>
                                </NavLink>
                            </li>
                            <li>
                                <NavLink to="/commercial/tasks" className={({ isActive }) => isActive ? 'active' : ''}>
                                    <span className="ico">{profileLevel < 2 ? '🔒' : '☑️'}</span>
                                    <span className="item-label">Tâches</span>
                                </NavLink>
                            </li>


                            <li className="commercial-section">
                                <span className="label" style={collapsed ? { display: 'none' } : {}}>Performance</span>
                            </li>
                            <li>
                                <NavLink to="/commercial/analytics" className={({ isActive }) => isActive ? 'active' : ''}>
                                    <span className="ico">{profileLevel < 2 ? '🔒' : '📊'}</span>
                                    <span className="item-label">Statistiques</span>
                                </NavLink>
                            </li>
                            <li>
                                <NavLink to="/commercial/challenges" className={({ isActive }) => isActive ? 'active' : ''}>
                                    <span className="ico">{profileLevel < 2 ? '🔒' : '🥇'}</span>
                                    <span className="item-label">Challenges</span>
                                </NavLink>
                            </li>
                            <li>
                                <NavLink to="/commercial/leaderboard" className={({ isActive }) => isActive ? 'active' : ''}>
                                    <span className="ico">{profileLevel < 2 ? '🔒' : '🏆'}</span>
                                    <span className="item-label">Classement</span>
                                </NavLink>
                            </li>
                            <li>

                            </li>

                        </div>
                        {/* SECRET ZONE */}
                        <li className="commercial-section">
                            <span className="label" style={collapsed ? { display: 'none' } : {}}>Espace Privé</span>
                        </li>

                        {!secretUnlock ? (
                            <li style={{ padding: '0 10px', marginTop: 4 }}>
                                <div style={{ background: 'rgba(0,0,0,0.2)', padding: '12px', borderRadius: 12, border: '1px dashed rgba(255,255,255,0.1)' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start', gap: 10, cursor: collapsed ? 'pointer' : 'default' }} onClick={collapsed ? handleBurger : undefined}>
                                        <span style={{ fontSize: '1.2rem' }}>🔒</span>
                                        {!collapsed && <span style={{ fontSize: '0.85rem', fontWeight: 600, opacity: 0.8 }}>Zone Verrouillée</span>}
                                    </div>
                                    {!collapsed && (
                                        <form onSubmit={handleSecretSubmit} style={{ marginTop: 10, display: 'flex', gap: 6 }}>
                                            <input
                                                type="password"
                                                value={secretCode}
                                                onChange={e => setSecretCode(e.target.value)}
                                                placeholder="Code..."
                                                style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: 'none', background: 'rgba(0,0,0,0.2)', color: 'inherit', fontSize: '0.9rem' }}
                                            />
                                            <button type="submit" style={{ background: 'var(--commercial-accent, #3b82f6)', color: 'white', border: 'none', borderRadius: 6, padding: '0 10px', cursor: 'pointer', fontWeight: 'bold' }}>OK</button>
                                        </form>
                                    )}
                                </div>
                            </li>
                        ) : (
                            <>
                                <li>
                                    <div style={{ padding: '8px 16px', fontSize: '0.8rem', opacity: 0.6, display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span>🔓</span> Déverrouillé
                                    </div>
                                </li>
                                <li>
                                    <NavLink to="/commercial/map" className={({ isActive }) => isActive ? 'active' : ''}>
                                        <span className="ico">🗺️</span>
                                        <span className="item-label">Carte</span>
                                    </NavLink>
                                </li>
                                <li>
                                    <NavLink to="/commercial/funnel" className={({ isActive }) => isActive ? 'active' : ''}>
                                        <span className="ico">📉</span>
                                        <span className="item-label">Entonnoir</span>
                                    </NavLink>
                                </li>
                                <li>
                                    <NavLink to="/commercial/export" className={({ isActive }) => isActive ? 'active' : ''}>
                                        <span className="ico">📤</span>
                                        <span className="item-label">Export</span>
                                    </NavLink>
                                </li>
                                <li>
                                    <NavLink to="/commercial/phoning" className={({ isActive }) => isActive ? 'active' : ''}>
                                        <span className="ico">📞</span>
                                        <span className="item-label">Phoning</span>
                                    </NavLink>
                                </li>

                                <li>
                                    <NavLink to="/commercial/resources" className={({ isActive }) => isActive ? 'active' : ''}>
                                        <span className="ico">📂</span>
                                        <span className="item-label">Ressources</span>
                                    </NavLink>
                                </li>
                                <li>
                                    <NavLink to="/commercial/templates" className={({ isActive }) => isActive ? 'active' : ''}>
                                        <span className="ico">💌</span>
                                        <span className="item-label">Modèles</span>
                                    </NavLink>
                                </li>
                                <li>
                                    <NavLink to="/commercial/availability" className={({ isActive }) => isActive ? 'active' : ''}>
                                        <span className="ico">📅</span>
                                        <span className="item-label">Agenda</span>
                                    </NavLink>
                                </li>


                            </>
                        )}

                    </ul>
                </nav>
            </aside>

            <main className={`commercial-main ${collapsed ? 'collapsed' : ''}`}>
                <header className="commercial-header">
                    <div className="header-left">
                        <button className="burger" onClick={handleBurger} aria-label="Menu">
                            <img src="/images/logoo.png" alt="Logo Info Colab" />
                        </button>
                        <span className="burger-label" onClick={handleBurger}>Menu</span>
                        <div className="header-title">{title}</div>
                    </div>

                    <div className="header-right">
                        {/* XP BAR */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginRight: 10 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                <div style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--commercial-accent)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Niveau {profileLevel}
                                </div>
                                <div style={{ width: 100, height: 6, background: 'var(--commercial-border)', borderRadius: 3, overflow: 'hidden', marginTop: 2 }}>
                                    <div style={{
                                        width: `${Math.min((profileLevel / 3) * 100, 100)}%`, // Level 1=33%, Level 2=66%, Level 3=100%
                                        height: '100%',
                                        background: 'linear-gradient(90deg, var(--commercial-accent), var(--commercial-accent-2))',
                                        transition: 'width 1s ease-out'
                                    }} />
                                </div>
                            </div>
                        </div>

                        <Link to="/" className="header-pill header-home-link" title="Retour au site">
                            <span className="header-home-ico">🏠</span>
                            <span className="header-home-label">Site Public</span>
                        </Link>

                        <button
                            type="button"
                            className="theme-toggle-btn"
                            onClick={() => setTheme(prev => (prev === 'dark' ? 'light' : 'dark'))}
                            title={theme === 'dark' ? 'Passer en clair' : 'Passer en sombre'}
                        >
                            {theme === 'dark' ? '☀️' : '🌙'}
                        </button>

                        <div className="user-menu" ref={userMenuRef}>
                            <button
                                type="button"
                                className="avatar-btn"
                                onClick={() => setUserMenuOpen(v => !v)}
                            >
                                {currentUser?.avatarUrl ? (
                                    <img src={currentUser.avatarUrl} alt="Avatar" className="avatar-img" />
                                ) : (
                                    <span className="avatar-fallback">👤</span>
                                )}
                            </button>
                            {userMenuOpen && (
                                <div className="user-dropdown">
                                    <ul>
                                        <li className="user-id-line">{currentUser?.name || currentUser?.email}</li>
                                        <li><NavLink to="/mon-compte" onClick={() => setUserMenuOpen(false)}>Mon profil</NavLink></li>
                                        <li><button type="button" onClick={() => { logout(); setUserMenuOpen(false); }}>Se déconnecter</button></li>
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                <div className="commercial-content">
                    {children}
                </div>
            </main>

            <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} mode={authMode} onModeChange={setAuthMode} />
        </div>
    );
}

export default CommercialLayout;
